import { Component, Element, Event, EventEmitter, h, Host, Method, Prop, State, Watch } from "@stencil/core";

import {
  StzhDatalistBigLabelChangeEvent,
  StzhDatalistDirection,
  StzhDatalistDirectionChangeEvent,
  StzhDatalistItemEntry,
  StzhDatalistVariant,
  StzhDatalistVariantChangeEvent,
} from "../../index";

import { addMediaChangeListener, media, removeMediaChangeListener } from "../../utils/media-utils";

/**
 * @slot - Slot for any content
 */
@Component({
  tag: "stzh-datalist",
  styleUrl: "stzh-datalist.scss",
  scoped: true,
})
export class StzhDatalist {
  /** Variant */
  @Prop({ reflect: true }) variant: StzhDatalistVariant = "default";

  /** With big label */
  @Prop({ reflect: true }) bigLabel: boolean = false;

  /** Columns */
  @Prop({ reflect: true }) columns: number = 1;

  /** Columns above small breakpoint */
  @Prop({ reflect: true }) columnsSmall: number;

  /** Columns above medium breakpoint  */
  @Prop({ reflect: true }) columnsMedium: number;

  /** Columns above large breakpoint  */
  @Prop({ reflect: true }) columnsLarge: number;

  /** Columns above ultra breakpoint  */
  @Prop({ reflect: true }) columnsUltra: number;

  /** Direction */
  @Prop({ reflect: true }) direction: StzhDatalistDirection = "vertical";

  /** Direction above small breakpoint */
  @Prop({ reflect: true }) directionSmall: StzhDatalistDirection;

  /** Direction above medium breakpoint  */
  @Prop({ reflect: true }) directionMedium: StzhDatalistDirection;

  /** Direction above large breakpoint  */
  @Prop({ reflect: true }) directionLarge: StzhDatalistDirection;

  /** Direction above ultra breakpoint  */
  @Prop({ reflect: true }) directionUltra: StzhDatalistDirection;
  /**Vertical Gap between items */
  @Prop({ reflect: true }) verticalGap: "default" | "small" = "default";

  /**
   * Whether to disable min-width of datalist item label for the `horizontal` variant.
   *
   * If `false`, the default `min-width` is `144px` (for >= 800px) and `120px` (for < 800px).
   * If `true`, the `min-width` is `none`, so the label column, will have the width of cell with the widest content.
   *
   * You can also overwrite the label min-width via CSS with the custom property `--item-label-min-width`.
   */
  // @Prop({ reflect: true }) disableLabelMinWidth: boolean = false;

  /**
   * Items in datalist
   * Array of objects that must at least include `value` attribute.
   * Can optionally contain `leadingIcon`, `label`, `meta`, `icon`, `iconLabel`, `iconTooltip`, `href`, `statusLabel`, `statusType` attribute and be nested to display deeper levels:
   * `[{"value":"Value 1", "label": "Label 1"}, {"value":"Value 2", "label": "Label 2", "items": [...]}]`
   */
  @Prop() items: StzhDatalistItemEntry[] | string = [];
  private _items: StzhDatalistItemEntry[] = [];

  /** Variant attribute change event */
  @Event() stzhVariantChange: EventEmitter<StzhDatalistVariantChangeEvent>;

  /** Big label attribute change event */
  @Event() stzhBigLabelChange: EventEmitter<StzhDatalistBigLabelChangeEvent>;

  /** Direction attribute change event */
  @Event() stzhDirectionChange: EventEmitter<StzhDatalistDirectionChangeEvent>;

  @Element() element: HTMLStzhDatalistElement;

  @Watch("direction")
  @Watch("directionSmall")
  @Watch("directionMedium")
  @Watch("directionLarge")
  @Watch("directionUltra")
  directionWatcher() {
    this.setCurrentDirection();
  }

  @Watch("columns")
  @Watch("columnsSmall")
  @Watch("columnsMedium")
  @Watch("columnsLarge")
  @Watch("columnsUltra")
  columnsWatcher() {
    this.setCurrentColumns();
  }

  @Watch("currentDirection")
  currentDirectionWatcher(newValue: StzhDatalistDirection) {
    this.stzhDirectionChange.emit({
      component: "stzh-datalist",
      value: newValue,
    });
  }

  @Watch("variant")
  variantWatcher(newValue: StzhDatalistVariant) {
    this.stzhVariantChange.emit({
      component: "stzh-datalist",
      value: newValue,
    });
  }

  @Watch("bigLabel")
  bigLabelWatcher(newValue: boolean) {
    this.stzhBigLabelChange.emit({
      component: "stzh-datalist",
      value: newValue,
    });
  }

  @Watch("items")
  itemsWatcher(newValue: any[] | string) {
    if (typeof newValue === "string") {
      this._items = JSON.parse(newValue);
    } else {
      this._items = newValue;
    }
  }

  /** Get current direction */
  @Method()
  async getCurrentDirection() {
    return this.currentDirection;
  }

  @State() currentColumns: number = 1;
  @State() currentDirection: StzhDatalistDirection;

  private setCurrentDirection = () => {
    if (media("ultra").matches && this.directionUltra) {
      this.currentDirection = this.directionUltra;
    } else if (media("large").matches && this.directionLarge) {
      this.currentDirection = this.directionLarge;
    } else if (media("medium").matches && this.directionMedium) {
      this.currentDirection = this.directionMedium;
    } else if (media("small").matches && this.directionSmall) {
      this.currentDirection = this.directionSmall;
    } else {
      this.currentDirection = this.direction;
    }
  };

  private setCurrentColumns = () => {
    if (media("ultra").matches && this.columnsUltra) {
      this.currentColumns = this.columnsUltra;
    } else if (media("large").matches && this.columnsLarge) {
      this.currentColumns = this.columnsLarge;
    } else if (media("medium").matches && this.columnsMedium) {
      this.currentColumns = this.columnsMedium;
    } else if (media("small").matches && this.columnsSmall) {
      this.currentColumns = this.columnsSmall;
    } else {
      this.currentColumns = this.columns;
    }
  };

  resizeHandler = () => {
    this.setCurrentDirection();
    this.setCurrentColumns();
  };

  async componentWillLoad() {
    this.itemsWatcher(this.items);
    this.bigLabelWatcher(this.bigLabel);
    this.resizeHandler();
  }

  connectedCallback() {
    addMediaChangeListener(this.resizeHandler);
  }

  disconnectedCallback() {
    removeMediaChangeListener(this.resizeHandler);
  }

  render() {
    const classes = {
      "stzh-datalist": true,
      "stzh-datalist--has-multiple-columns": this.currentColumns > 1,
      "stzh-datalist--big-label": this.bigLabel,
      [`stzh-datalist--direction-${this.currentDirection}`]: !!this.currentDirection,
      [`stzh-datalist--${this.variant}`]: !!this.variant,
      [`stzh-datalist--vertical-gap-${this.verticalGap}`]: !!this.verticalGap,
    };

    return (
      <Host
        is-direction={this.currentDirection}
        vertical-gap={this.verticalGap}
        style={{
          "--columns": this.currentColumns ? this.currentColumns.toString() : null,
        }}
      >
        <div class={classes}>
          <div class="stzh-datalist__wrapper">
            <div class="stzh-datalist__list" role="list">
              {this._items.map(item => (
                <stzh-datalist-item {...item} hasNested={!!item.items}>
                  {item.items && item.items.length > 0 && <stzh-datalist items={item.items}></stzh-datalist>}
                </stzh-datalist-item>
              ))}
              <slot></slot>
            </div>
          </div>
        </div>
      </Host>
    );
  }
}
